:root {
  font-family: var(--ant-font-family);
  color: var(--ant-color-text);
  background: var(--ant-color-bg-layout);
  font-synthesis: none;
  text-rendering: optimizeLegibility;
}

* {
  box-sizing: border-box;
}

body {
  min-width: 0;
  min-height: 100vh;
  margin: 0;
}

#root {
  min-width: 0;
}

a {
  color: var(--ant-color-link);
}

.app-loading {
  display: grid;
  min-height: 100vh;
  background: var(--ant-color-bg-layout);
  place-items: center;
}

.login-page {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(430px, 0.75fr);
  min-height: 100vh;
  overflow: hidden;
  background: var(--ant-color-bg-layout);
}

.login-story {
  --login-story-start: var(--ant-color-primary-active);
  --login-story-end: color-mix(
    in srgb,
    var(--ant-color-primary-active) 78%,
    var(--ant-color-text-base) 22%
  );

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 9vw;
  overflow: hidden;
  color: var(--ant-color-text-light-solid);
  background:
    linear-gradient(130deg, var(--login-story-start), var(--login-story-end)),
    repeating-linear-gradient(
      90deg,
      transparent 0 179px,
      color-mix(in srgb, var(--ant-color-primary-bg) 18%, transparent) 180px
    );
}

.login-story::after {
  position: absolute;
  right: -13vw;
  bottom: -22vw;
  width: 48vw;
  height: 48vw;
  border: 1px solid color-mix(in srgb, var(--ant-color-primary-bg) 28%, transparent);
  border-radius: 50%;
  content: '';
}

.login-story h1 {
  z-index: 1;
  max-width: 740px;
  margin: 24px 0;
  color: inherit;
  font-size: clamp(48px, 5.5vw, 86px);
  font-weight: 760;
  line-height: 1.08;
  letter-spacing: -0.06em;
}

.login-story p {
  z-index: 1;
  max-width: 520px;
  color: inherit;
  font-size: 18px;
}

.login-story .login-story-intro {
  color: inherit;
}

.login-kicker,
.story-stat {
  z-index: 1;
}

.login-kicker {
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.24em;
}

.story-stat {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 60px;
}

.story-stat strong {
  font-size: 42px;
  font-variant-numeric: tabular-nums;
}

.court-orbit {
  position: absolute;
  top: 8vh;
  right: 8vw;
  width: 128px;
  height: 128px;
  border: 30px solid color-mix(in srgb, var(--ant-color-primary-bg) 20%, transparent);
  border-radius: 50% 50% 50% 10%;
  transform: rotate(28deg);
}

.login-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(48px, 7vw, 110px);
  border-left: 1px solid var(--ant-color-border-secondary);
}

.login-panel h2 {
  margin: 0 0 8px;
  font-size: 34px;
  letter-spacing: -0.035em;
}

.login-alert {
  margin: 16px 0;
}

.security-note {
  margin-top: 22px;
  color: var(--ant-color-text-secondary);
  line-height: 1.7;
}

button,
a,
[role='menuitem'] {
  outline-offset: 3px;
}

.app-shell {
  width: 100%;
  height: 100vh;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.app-sider {
  position: relative;
  overflow: hidden;
}

.app-sider-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
}

.app-sider::after {
  position: absolute;
  right: -92px;
  bottom: 72px;
  width: 220px;
  height: 220px;
  border: 1px solid color-mix(in srgb, var(--ant-color-primary-bg) 24%, transparent);
  border-radius: 50%;
  content: '';
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 88px;
  padding: 0 22px;
  color: var(--ant-color-text-light-solid);
  text-decoration: none;
}

.app-sider-collapsed .brand {
  justify-content: center;
  padding: 0;
}

.app-sider-collapsed .brand-copy {
  display: none;
}

.brand-mark {
  flex: 0 0 auto;
  display: grid;
  width: 38px;
  height: 38px;
  color: var(--ant-color-primary);
  font-size: 17px;
  font-weight: 800;
  background: var(--ant-color-primary-bg);
  border-radius: 50% 50% 50% 10%;
  place-items: center;
}

.sider-collapse-button {
  flex: 0 0 auto;
  align-self: stretch;
  margin: 0 14px 12px;
  color: var(--ant-color-text-light-solid);
  background: transparent;
}

.app-sider-content .sider-collapse-button:not(:disabled):hover,
.app-sider-content .sider-collapse-button:not(:disabled):focus-visible {
  color: var(--ant-color-text-light-solid);
  background: color-mix(in srgb, var(--ant-color-text-light-solid) 18%, transparent);
}

.app-sider-content .sider-collapse-button:not(:disabled):active {
  color: var(--ant-color-text-light-solid);
  background: color-mix(in srgb, var(--ant-color-text-light-solid) 28%, transparent);
}

.app-sider-content .sider-collapse-button:focus-visible {
  outline: 2px solid var(--ant-color-primary-border-hover);
}

.app-sider-collapsed .sider-collapse-button {
  margin-right: 8px;
  margin-left: 8px;
  padding-inline: 4px;
}

.app-navigation {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.navigation-mark {
  font-size: 13px;
  font-weight: 700;
}

.brand strong,
.brand small {
  display: block;
}

.brand strong {
  font-size: 16px;
  letter-spacing: 0.08em;
}

.brand small {
  margin-top: 3px;
  color: inherit;
  font-size: 9px;
  letter-spacing: 0.2em;
}

.sider-footnote {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  padding: 18px 24px 24px;
  color: var(--ant-color-text-light-solid);
  font-size: 12px;
}

.app-main-layout {
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.list-filter-bar {
  flex-wrap: wrap;
}

.reconciliation-card {
  margin-bottom: 24px;
  overflow: hidden;
}

.reconciliation-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 22px;
}

.reconciliation-title h4 {
  margin: 0 0 4px;
}

.reconciliation-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 1px;
  overflow: hidden;
  border: 1px solid var(--ant-color-border-secondary);
  border-radius: var(--ant-border-radius);
  background: var(--ant-color-border-secondary);
}

.reconciliation-stat {
  padding: 16px;
  background: var(--ant-color-bg-container);
}

.reconciliation-checks {
  margin: 18px 0;
}

.reconciliation-issues {
  margin-top: 16px;
}

.audit-change-table {
  margin-top: 12px;
}

.audit-technical-section {
  margin-top: 24px;
}

.app-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  min-height: 64px;
  height: auto;
  gap: 16px;
  padding: 12px 32px;
  border-bottom: 1px solid var(--ant-color-border-secondary);
  background: var(--ant-color-bg-container);
}

.app-header-actions {
  min-width: 0;
  justify-content: flex-end;
}

.venue-name {
  font-weight: 650;
  letter-spacing: 0.03em;
}

.header-action {
  color: var(--ant-color-text-secondary);
  cursor: default;
}

.app-content {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  padding: 52px 56px;
  overflow-x: auto;
  overflow-y: auto;
  background-color: var(--ant-color-bg-layout);
  background-image:
    linear-gradient(
      90deg,
      transparent 49.8%,
      color-mix(in srgb, var(--ant-color-primary) 5%, transparent) 50%,
      transparent 50.2%
    ),
    linear-gradient(
      0deg,
      transparent 49.8%,
      color-mix(in srgb, var(--ant-color-primary) 5%, transparent) 50%,
      transparent 50.2%
    );
  background-size: 260px 260px;
}

.app-content::before {
  position: absolute;
  top: -240px;
  right: 0;
  width: 620px;
  height: 620px;
  border: 1px solid color-mix(in srgb, var(--ant-color-primary) 14%, transparent);
  border-radius: 50%;
  content: '';
}

.content-inner,
.app-breadcrumb {
  position: relative;
  z-index: 1;
  max-width: 1180px;
  min-width: 0;
  width: 100%;
  margin-right: auto;
  margin-left: auto;
}

.app-breadcrumb {
  margin-bottom: 16px;
}

.compact-heading {
  align-items: center;
  margin-bottom: 24px;
}

.compact-heading h1 {
  margin-bottom: 6px;
  font-size: 38px;
}

.detail-card,
.password-card {
  border-color: var(--ant-color-border-secondary);
  box-shadow: var(--ant-box-shadow-secondary);
}

.filter-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px;
  background: var(--ant-color-bg-container);
  border-radius: var(--ant-border-radius);
}

.list-filter-bar,
.list-filter-primary,
.list-filter-extra,
.list-filter-active {
  align-items: flex-end;
}

.list-filter-primary {
  flex: 1 1 560px;
}

.list-filter-actions {
  justify-content: flex-end;
  margin-left: auto;
}

.shared-page-header .page-action-group {
  flex-shrink: 0;
}

.list-filter-primary,
.list-filter-extra,
.list-filter-active {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.list-filter-extra,
.list-filter-active {
  flex-basis: 100%;
}

.list-filter-active {
  padding-top: 4px;
}

.confirmation-form {
  margin-top: 0;
}

.impact-list {
  margin: 8px 0;
  padding-left: 22px;
}

.business-date-range {
  display: grid;
  gap: 5px;
  color: var(--ant-color-text-secondary);
  font-size: 12px;
}

.business-date-range {
  min-width: 330px;
}

.business-date-range-label {
  line-height: 1.2;
}

.business-date-range-error {
  font-size: 12px;
}

@media (max-width: 720px) {
  .business-date-range-popup {
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
  }

  .business-date-range-popup-container {
    max-width: 100%;
    max-height: min(180px, calc(100vh - 16px));
    overflow: auto;
    overscroll-behavior-inline: contain;
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.commission-totals {
  margin-bottom: 18px;
}

.commission-summary-card {
  height: 100%;
}

.commission-total-card {
  border-color: var(--ant-color-primary-border);
}

.drawer-actions {
  position: sticky;
  bottom: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
  margin-top: 20px;
  padding: 12px 0;
  background: var(--ant-color-bg-elevated);
  border-top: 1px solid var(--ant-color-border-secondary);
}

.drawer-readonly-summary {
  margin-bottom: 20px;
}

.business-form {
  width: 100%;
  margin-top: 20px;
}

.business-form-wide {
  margin-top: 16px;
}

.student-edit-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.student-edit-wide,
.student-edit-form > .drawer-actions {
  grid-column: 1 / -1;
}

.catalog-rule-alert {
  margin-bottom: 16px;
}

.catalog-form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.catalog-detail-card {
  padding: 24px;
  background: var(--ant-color-bg-container);
  border: 1px solid var(--ant-color-border-secondary);
  border-radius: var(--ant-border-radius-lg);
  box-shadow: var(--ant-box-shadow-secondary);
}

.consumption-step-card {
  margin-bottom: 20px;
}

.consumption-page-error {
  margin-bottom: 16px;
}

.consumption-package-field {
  max-width: 680px;
  margin-bottom: 0;
}

.step-number {
  display: inline-grid;
  width: 34px;
  height: 34px;
  color: var(--ant-color-primary);
  font-family: var(--ant-font-family-code);
  font-size: 12px;
  background: var(--ant-color-primary-bg);
  border-radius: 50% 50% 50% 10%;
  place-items: center;
}

.consumption-grid {
  display: grid;
  grid-template-columns: minmax(0, 900px);
}

.consumption-line-list,
.consumption-review,
.consumption-assistant-formulas {
  width: 100%;
}

.consumption-create-page .consumption-line-list {
  isolation: isolate;
}

.consumption-create-page .business-form {
  padding-bottom: var(--consumption-submit-clearance, 0px);
}

.consumption-table-field {
  margin-bottom: 0;
}

.consumption-assistant-formulas .consumption-table-field + .consumption-table-field {
  margin-top: 10px;
}

.drawer-task-alert {
  margin-bottom: 20px;
}

.consumption-audit-item p {
  margin-bottom: 4px;
}

.consumption-submit-bar {
  position: sticky;
  bottom: 20px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: color-mix(in srgb, var(--ant-color-bg-container) 94%, transparent);
  border: 1px solid var(--ant-color-border-secondary);
  border-radius: var(--ant-border-radius-lg);
  box-shadow: var(--ant-box-shadow-secondary);
  backdrop-filter: blur(12px);
}

@media (max-width: 900px) {
  .consumption-grid {
    grid-template-columns: 1fr;
  }

  .consumption-submit-bar {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
    bottom: 12px;
    padding: 14px;
  }

  .consumption-submit-button {
    width: 100%;
  }
}

@media (max-width: 960px) {
  .login-page {
    grid-template-columns: minmax(0, 1fr);
    overflow: auto;
  }

  .login-story {
    display: none;
  }

  .login-panel {
    min-width: 0;
    padding: clamp(32px, 8vw, 72px);
    border-left: 0;
  }

  .app-header {
    padding-inline: 20px;
  }

  .venue-name {
    flex: 1 1 auto;
  }

  .app-content {
    padding: 28px 24px;
  }
}

@media (max-width: 720px) {
  .daily-task-page .shared-page-header {
    align-items: stretch;
    flex-direction: column;
    gap: 16px;
  }

  .daily-task-page .shared-page-header .page-action-group {
    align-self: flex-end;
  }

  .filter-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .list-filter-primary,
  .list-filter-extra {
    display: grid;
    align-items: stretch;
    grid-template-columns: minmax(0, 1fr);
  }

  .list-filter-primary > *,
  .list-filter-extra > * {
    width: 100% !important;
    min-width: 0;
  }

  .list-filter-actions {
    justify-content: flex-start;
    width: 100%;
    margin-left: 0;
  }

  .business-date-range {
    min-width: 0;
  }

  .app-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .app-header-actions {
    justify-content: flex-start;
  }

  .app-content {
    padding: 20px 16px;
  }

  .student-edit-form,
  .catalog-form-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.formula-expression,
.formula-preview code,
.formula-detail code {
  font-family: var(--ant-font-family-code);
  color: var(--ant-color-text);
}

.formula-name-link {
  display: block;
  width: 100%;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.formula-token-bar {
  margin: -8px 0 20px;
}

.formula-preview {
  margin-top: 24px;
  padding: 20px;
  background: var(--ant-color-fill-tertiary);
  border: 1px solid var(--ant-color-border-secondary);
  border-radius: var(--ant-border-radius-lg);
}

.formula-preview ol {
  max-height: 220px;
  padding-left: 24px;
  overflow: auto;
}

.formula-detail {
  margin-top: 20px;
}

.form-note {
  margin: 4px 0 24px;
  padding: 14px 16px;
  color: var(--ant-color-text-secondary);
  line-height: 1.7;
  background: var(--ant-color-fill-tertiary);
  border-left: 3px solid var(--ant-color-primary);
}

.page-error {
  margin: 12px 0;
  padding: 14px 16px;
  color: var(--ant-color-error);
  background: var(--ant-color-error-bg);
  border: 1px solid var(--ant-color-error-border);
  border-radius: var(--ant-border-radius);
}

.detail-card {
  margin-bottom: 20px;
}

.voucher-thumbnail-button {
  height: auto;
  padding: 3px;
}

.password-card {
  width: 560px;
  margin: 5vh auto 0;
}

.dashboard-section {
  margin-bottom: 32px;
}

.dashboard-section-heading {
  gap: 12px;
  margin-bottom: 16px;
}

.dashboard-section-heading h2 {
  margin: 0;
}

.dashboard-action-card,
.dashboard-data-card {
  height: 100%;
}

.dashboard-action-card h3 {
  margin: 10px 0 8px;
}

.dashboard-action-card p {
  min-height: 44px;
  color: var(--ant-color-text-secondary);
}

.dashboard-action-index {
  color: var(--ant-color-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
}

.dashboard-data-grid {
  align-items: stretch;
}

.dashboard-row-clip {
  max-width: 100%;
  min-width: 0;
  overflow-x: clip;
}

.dashboard-data-content {
  min-height: 220px;
}

.formula-embedded-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.task-feed {
  padding: 0;
  margin: 0;
  list-style: none;
}

.task-feed-item {
  padding: 14px 0;
  border-bottom: 1px solid var(--ant-color-border-secondary);
}

.task-feed-item:first-child {
  padding-top: 0;
}

.task-feed-item:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.page-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 36px;
}

.page-heading h1 {
  margin: 8px 0 12px;
  font-size: clamp(36px, 4vw, 56px);
  font-weight: 760;
  letter-spacing: -0.045em;
}

.page-heading p {
  max-width: 620px;
  color: var(--ant-color-text-secondary);
  font-size: 16px;
}

.eyebrow {
  color: var(--ant-color-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
}

@media (forced-colors: none) {
  main.app-content .commission-plan-page > .ant-typography-secondary,
  main.app-content .student-detail-page .purchase-history-caption,
  main.app-content .audit-page > .ant-result-403 > .ant-result-subtitle {
    color: var(--ant-color-text-secondary);
  }

  main.app-content
    .list-page
    a[href^='/employees/']:not(.app-accessible-button):not([disabled]):not([aria-disabled='true']) {
    color: #0958d9;
  }

  main.app-content
    .list-page
    a[href^='/employees/']:not(.app-accessible-button):not([disabled]):not(
      [aria-disabled='true']
    ):hover {
    color: #003eb3;
  }

  main.app-content
    .list-page
    a[href^='/employees/']:not(.app-accessible-button):not([disabled]):not(
      [aria-disabled='true']
    ):active {
    color: #002c8c;
  }

  button.app-accessible-button:not(:disabled):not([aria-disabled='true']):focus-visible,
  a.app-accessible-button:not([aria-disabled='true']):focus-visible {
    outline: 3px solid #0958d9;
    outline-offset: 2px;
  }
}

@media (forced-colors: active) {
  button:focus-visible,
  a:focus-visible,
  [role='menuitem']:focus-visible {
    outline: 2px solid Highlight;
  }

  button.app-accessible-button:not(:disabled):not([aria-disabled='true']):focus-visible,
  a.app-accessible-button:not([aria-disabled='true']):focus-visible {
    outline: 2px solid Highlight;
    outline-offset: 2px;
  }

  .page-error {
    color: CanvasText;
    background: Canvas;
    border-color: CanvasText;
  }
}

@media (max-width: 1280px) {
  .app-content {
    padding: 40px 36px;
  }
}

@media (max-width: 1365px) {
  .login-story {
    padding: 7vw;
  }

  .login-story h1 {
    font-size: 52px;
  }

  .login-panel {
    padding: 52px;
  }
}
